import {chain} from 'lodash';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  getCalendarEventReminders,
  makeCalendarEventVersionColor,
} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {htmlToText} from '../../../../srcCommon/services/converters/htmlToTextConverter';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {AttachmentModel} from '../../../models/api/attachmentModel';
import {CalendarEventModel, CalendarEventStatusesEnum} from '../../../models/api/calendarEventModel';
import {
  CalendarEventVersionModel,
  CalendarEventVersionVisibilityTypesEnum,
} from '../../../models/api/calendarEventVersionModel';
import {CalendarVersionAccessRolesEnum, CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {CalendarEventData} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {CalendarsPanelId} from '../../../store/panels/panelStateBase';
import {CalendarEventTimeFormatter} from '../../common/formatters/calendarEventTimeFormatter';
import {AttachmentBaseContainer} from '../../inboxes/viewer/activity/attachments/attachmentBaseContainer';
import {buildCalendarEventDescriptionHtml} from '../calendarEventHelpers';
import {StyledAttachmentDiv, StyledAttachmentsGridDiv} from '../editor/calendarEventEditorWithNotifyGuests';
import {CalendarEventParticipantsList} from '../participants/calendarEventParticipantsList';
import {CalendarEventParticipantsSummary} from '../participants/calendarEventParticipantsSummary';
import {CalendarRemindersViewer} from '../reminders/calendarRemindersViewer';
import {CalendarEventDetailRow} from './calendarEventDetailRow';
import {
  maybeRenderConferenceRow,
  maybeRenderDescriptionRow,
  maybeRenderLocationsRow,
  StyledCalendarNameDiv,
  StyledEventColorDiv,
  StyledSummaryDiv,
  StyledSummaryWrapperDiv,
  StyledTextDiv,
} from './calendarEventViewerContentHelpers';

/*
 * Props.
 */

export interface CalendarEventViewerContentProps {
  panelId: CalendarsPanelId;
  calendarEventData: CalendarEventData;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  timeFormat: TimeFormatsEnum;
  hasCalendarAttachments: boolean;
}

/*
 * Style.
 */

const StyledVisibilityContentDiv = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 3px;
  align-items: center;
`;

/*
 * Component.
 */

export const CalendarEventViewerContent: FC<CalendarEventViewerContentProps> = (props) => {
  const {calendarEventData, visualParticipants, timeFormat, panelId} = props;
  const {calendarEventVersion, calendarEvent, calendarVersion} = calendarEventData;
  const {channel, instanceStartDate} = calendarEventData;

  const {description} = calendarEventVersion;
  const channelType = channel ? channel.type : undefined;

  const descriptionHtml = useMemo(
    () => buildCalendarEventDescriptionHtml(description, channelType),
    [description, channelType],
  );
  const descriptionIsEmpty = useMemo(() => htmlToText(description || '').trim().length === 0, [description]);

  return (
    <>
      <CalendarEventDetailRow
        iconContent={renderTitleSectionIcon(calendarEventVersion, calendarVersion)}
        content={renderSummarySection(calendarEventVersion, calendarEvent, calendarVersion)}
      />
      <CalendarEventDetailRow
        iconName="snoozeSmall"
        content={
          <StyledTextDiv>
            <CalendarEventTimeFormatter
              timeFormat={timeFormat}
              calendarEventVersion={calendarEventVersion}
              instanceStartDate={instanceStartDate}
              shouldUseMultiline
            />
          </StyledTextDiv>
        }
      />
      {maybeRenderLocationsRow(calendarEventVersion)}
      {maybeRenderConferenceRow(calendarEventVersion)}
      {maybeRenderParticipantsRow(panelId, visualParticipants)}
      {props.hasCalendarAttachments && maybeRenderAttachmentsRow(calendarEventVersion.attachments)}
      {maybeRenderDescriptionRow(descriptionHtml, descriptionIsEmpty)}
      {maybeRenderRemindersRow(calendarVersion, calendarEventVersion)}
      {maybeRenderVisibilityRow(calendarEventVersion)}
      <CalendarEventDetailRow
        iconName="calendarSmall"
        content={renderCalendarContent(props.calendarEventData)}
      />
    </>
  );
};

function renderTitleSectionIcon(
  calendarEventVersion: CalendarEventVersionModel,
  calendarVersion: CalendarVersionModel,
) {
  const eventColor = makeCalendarEventVersionColor(calendarEventVersion, calendarVersion);
  return <StyledEventColorDiv $color={eventColor} />;
}

function renderSummarySection(
  calendarEventVersion: CalendarEventVersionModel,
  calendarEvent: CalendarEventModel,
  calendarVersion: CalendarVersionModel,
) {
  return (
    <StyledSummaryWrapperDiv>
      <StyledSummaryDiv $isCancelled={calendarEvent.status === CalendarEventStatusesEnum.CANCELLED}>
        {renderSummary(calendarEventVersion, calendarVersion)}
      </StyledSummaryDiv>
    </StyledSummaryWrapperDiv>
  );
}

function renderSummary(
  calendarEventVersion: CalendarEventVersionModel,
  calendarVersion: CalendarVersionModel,
) {
  if (calendarVersion.accessRole === CalendarVersionAccessRolesEnum.FREE_BUSY) {
    return <Trans id="calendar-event-viewer-free-busy-event">Busy</Trans>;
  }

  if (
    calendarEventVersion.visibility === CalendarEventVersionVisibilityTypesEnum.PRIVATE &&
    !calendarEventVersion.summary &&
    calendarVersion.accessRole === CalendarVersionAccessRolesEnum.READER
  ) {
    return <Trans id="calendar-event-viewer-private-event">Private event</Trans>;
  }

  if (!calendarEventVersion.summary) {
    return <Trans id="calendar-event-viewer-untitled-event">(Untitled event)</Trans>;
  }

  return calendarEventVersion.summary;
}

function maybeRenderParticipantsRow(
  panelId: CalendarsPanelId,
  visualParticipants: ReadonlyArray<VisualParticipantModel>,
) {
  const attendees = visualParticipants.filter((p) => p.isAttendee);
  if (attendees.length === 0) {
    return null;
  }

  return (
    <CalendarEventDetailRow
      iconName="teammatesSmall"
      content={renderParticipantsContent(panelId, attendees)}
    />
  );
}

function renderParticipantsContent(
  panelId: CalendarsPanelId,
  attendees: ReadonlyArray<VisualParticipantModel>,
) {
  return (
    <div>
      <CalendarEventParticipantsSummary visualParticipants={attendees} />
      <CalendarEventParticipantsList visualParticipants={attendees} panelId={panelId} />
    </div>
  );
}

function maybeRenderAttachmentsRow(attachments: ReadonlyArray<AttachmentModel>) {
  if (attachments.length === 0) {
    return null;
  }
  return <CalendarEventDetailRow iconName="attachmentSmall" content={renderAttachments(attachments)} />;
}

function renderAttachments(attachments: ReadonlyArray<AttachmentModel>) {
  if (attachments.length === 0) {
    return null;
  }
  return (
    <StyledAttachmentsGridDiv $isEventEditor={false}>
      {chain(attachments)
        .sortBy((attachment) => attachment.name.toLowerCase())
        .map((attachment) => (
          <StyledAttachmentDiv>
            <AttachmentBaseContainer attachment={attachment} />
          </StyledAttachmentDiv>
        ))
        .value()}
    </StyledAttachmentsGridDiv>
  );
}

function maybeRenderRemindersRow(
  calendarVersion: CalendarVersionModel,
  calendarEventVersion: CalendarEventVersionModel,
) {
  const remindersToDisplay = getCalendarEventReminders(calendarVersion, calendarEventVersion);
  if (remindersToDisplay.length === 0) {
    return null;
  }

  return (
    <CalendarEventDetailRow
      iconName="subscribeSmall"
      content={<CalendarRemindersViewer reminders={remindersToDisplay} />}
    />
  );
}

function maybeRenderVisibilityRow(calendarEventVersion: CalendarEventVersionModel) {
  if (calendarEventVersion.visibility !== CalendarEventVersionVisibilityTypesEnum.PRIVATE) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconName="eyeSmall"
          content={
            <StyledVisibilityContentDiv>
              <Trans id="calendar-event-viewer-private">Private</Trans>
              <Icon name="lockFilledTiny" color={theme.greys.shade70} />
            </StyledVisibilityContentDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

function renderCalendarContent(calendarEventData: CalendarEventData) {
  const {calendar, calendarVersion} = calendarEventData;
  return <StyledCalendarNameDiv>{calendarVersion.nameOverride || calendar.name}</StyledCalendarNameDiv>;
}
